"use client";
import { useEffect } from "react";

// WebMCP (navigator.modelContext, W3C Community Draft фев-2026): объявляем наши
// конверсионные формы как инструменты для ИИ-агентов (Gemini в Chrome 149+, Edge 147+).
// Агент сможет отправить заявку на покупку/аренду прямо из диалога → прямой канал
// лидов из ИИ. Логику не дублируем — tools зовут те же /api/contact и /api/rent, что
// и формы. Нативный API: если браузер его не поддерживает, компонент — тихий no-op
// (никакого 310KB-полифилла всем посетителям).
//
// Правило спеки: регистрируем tools когда соответствующий UI доступен, снимаем при
// unmount. Формы (покупка внизу, аренда в модалке) присутствуют на странице всегда,
// поэтому регистрируем на маунте лэйаута и снимаем при выходе.

async function postJson(url: string, body: Record<string, unknown>) {
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, status: res.status, data };
}

export default function WebMcp() {
  useEffect(() => {
    const mc = typeof navigator !== "undefined" ? navigator.modelContext : undefined;
    if (!mc || typeof mc.registerTool !== "function") return; // браузер без WebMCP → no-op

    const TOOLS: WebMcpTool[] = [
      {
        name: "submit_rental_request",
        description:
          "Submit a rental inquiry for an apartment at Whale Marina Condo (Pattaya, Thailand). " +
          "A sales manager will call the visitor back. Use when the user wants to RENT an apartment.",
        inputSchema: {
          type: "object",
          required: ["name", "phone"],
          properties: {
            name: { type: "string", description: "Visitor's full name" },
            phone: {
              type: "string",
              description: "Visitor's phone number in international format, e.g. +66...",
            },
          },
        },
        execute: async (input) => {
          const { name, phone } = input as { name?: string; phone?: string };
          if (!name || !phone) return { success: false, error: "name and phone are required" };
          const r = await postJson("/api/rent", { name, phone });
          return r.ok
            ? { success: true, message: "Rental request sent. The manager will call back shortly." }
            : { success: false, error: r.data?.error || `Request failed (${r.status})` };
        },
      },
      {
        name: "submit_purchase_inquiry",
        description:
          "Submit a purchase inquiry for buying an apartment at Whale Marina Condo (Pattaya, Thailand). " +
          "Apartments start from 2.3M THB (studios and 1-bedroom units). Use when the user wants to BUY.",
        inputSchema: {
          type: "object",
          required: ["name", "phone"],
          properties: {
            name: { type: "string", description: "Visitor's full name" },
            phone: {
              type: "string",
              description: "Visitor's phone number in international format, e.g. +66...",
            },
            apartmentType: {
              type: "string",
              description: "Optional desired apartment type, e.g. 'Studio', '1 Bedroom' or '2 Bedroom'",
            },
            comment: { type: "string", description: "Optional message or questions" },
          },
        },
        execute: async (input) => {
          const { name, phone, apartmentType, comment } = input as {
            name?: string;
            phone?: string;
            apartmentType?: string;
            comment?: string;
          };
          if (!name || !phone) return { success: false, error: "name and phone are required" };
          const r = await postJson("/api/contact", { name, phone, apartmentType, comment });
          return r.ok
            ? { success: true, message: "Purchase inquiry sent. The sales team will contact the visitor." }
            : { success: false, error: r.data?.error || `Request failed (${r.status})` };
        },
      },
    ];

    // registerTool может вернуть функцию-деинсталлятор; иначе снимаем через unregisterTool.
    const disposers: Array<() => void> = [];
    for (const tool of TOOLS) {
      try {
        const maybeDispose = mc.registerTool(tool);
        if (typeof maybeDispose === "function") disposers.push(maybeDispose);
      } catch {
        // не роняем страницу, если конкретный tool не зарегистрировался
      }
    }

    return () => {
      for (const d of disposers) {
        try {
          d();
        } catch {
          /* noop */
        }
      }
      if (typeof mc.unregisterTool === "function") {
        for (const tool of TOOLS) {
          try {
            mc.unregisterTool(tool.name);
          } catch {
            /* noop */
          }
        }
      }
    };
  }, []);

  return null;
}
